import { useState } from 'react';
import { X, CalendarCheck, CalendarClock } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

export type ModifyOption = 'include_current' | 'from_next';

interface ModifyRecurrenceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (option: ModifyOption) => void;
}

export function ModifyRecurrenceDialog({
  isOpen,
  onClose,
  onConfirm,
}: ModifyRecurrenceDialogProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<ModifyOption>('include_current');

  if (!isOpen) return null;

  const options: { key: ModifyOption; icon: typeof CalendarCheck; title: string; desc: string }[] =
    [
      {
        key: 'include_current',
        icon: CalendarCheck,
        title: t('calendar.modify.includeCurrent' as any),
        desc: t('calendar.modify.includeCurrentDesc' as any),
      },
      {
        key: 'from_next',
        icon: CalendarClock,
        title: t('calendar.modify.fromNext' as any),
        desc: t('calendar.modify.fromNextDesc' as any),
      },
    ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {t('calendar.modify.title' as any)}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Options */}
        <div className="p-6 space-y-3">
          {options.map((opt) => {
            const Icon = opt.icon;
            const isSelected = selected === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setSelected(opt.key)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  isSelected
                    ? 'border-[#16c47f] bg-[#16c47f]/5'
                    : 'border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? 'bg-[#16c47f]/10 text-[#16c47f]'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p
                      className={`text-sm font-semibold ${
                        isSelected ? 'text-[#16c47f]' : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {opt.title}
                    </p>
                    <div className="mt-1 space-y-0.5">
                      {opt.desc.split('\n').map((line, i) => (
                        <p
                          key={i}
                          className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed"
                        >
                          → {line}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            {t('calendar.modify.cancel' as any)}
          </button>
          <button
            onClick={() => onConfirm(selected)}
            className="px-4 py-2 text-sm font-medium text-white bg-[#16c47f] hover:bg-[#14b371] rounded-xl transition-colors"
          >
            {t('calendar.modify.apply' as any)}
          </button>
        </div>
      </div>
    </div>
  );
}
